import { useState, useEffect, useRef } from "react";
import { Badge, Button, Form, FormGroup, Input, Label } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faGlobe,
  faKey,
  faLock,
  faSpinner,
  faTimes,
  faUser,
  faUserLock,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import Auth from "../auth/Auth";
import { CyProfile } from "./CyProfile";
import { ContainerWithHeaderAndMenu } from "../menuandheader/ContainerWithHeaderAndMenu";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "../i18n/LanguageSwitcher";
import { useProfileQuery } from "./useProfileQuery";
import { useUpdateProfileMutation } from "./useUpdateProfileMutation";

interface Props {
  auth: Auth;
}

export default function Profile(props: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { profile, fetching } = useProfileQuery();
  const [, executeUpdateProfile] = useUpdateProfileMutation();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [isInitialized, setIsInitialized] = useState(false);
  const [saveStatus, setSaveStatus] = useState<
    "idle" | "saving" | "saved" | "error"
  >("idle");

  useEffect(() => {
    if (profile && !isInitialized) {
      setFirstName(profile.firstName ?? "");
      setLastName(profile.lastName ?? "");
      setIsInitialized(true);
    }
  }, [profile, isInitialized]);

  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  const handleSave = async (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }
    setSaveStatus("saving");
    const result = await executeUpdateProfile({
      firstName: firstName.trim() || null,
      lastName: lastName.trim() || null,
    });
    if (result.error) {
      setSaveStatus("error");
    } else {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      setSaveStatus("saved");
      saveTimeoutRef.current = setTimeout(() => {
        setSaveStatus((current) => (current === "saved" ? "idle" : current));
      }, 3000);
    }
  };

  return (
    <ContainerWithHeaderAndMenu icon={faUser} title={t("profile.title")}>
      <h5>
        <FontAwesomeIcon icon={faGlobe} className="me-1" />
        {t("profile.language")}
      </h5>
      <FormGroup>
        <LanguageSwitcher />
      </FormGroup>

      <h5>
        <FontAwesomeIcon icon={faUser} className="me-1" />
        {t("profile.user")}
      </h5>
      <p>
        <FontAwesomeIcon icon={faUserLock} className="me-1" />
        {props.auth.getEmail()}
      </p>

      <h5>
        <FontAwesomeIcon icon={faUser} className="me-1" />
        {t("profile.personalInfo")}
      </h5>
      <Form
        onSubmit={handleSave}
        className="mb-4"
        data-cy={CyProfile.ProfileForm}
      >
        <FormGroup>
          <Label for="profile-first-name">{t("profile.firstName")}</Label>
          <Input
            id="profile-first-name"
            name="firstName"
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            disabled={fetching || saveStatus === "saving"}
            data-cy={CyProfile.FirstNameInput}
          />
        </FormGroup>
        <FormGroup>
          <Label for="profile-last-name">{t("profile.lastName")}</Label>
          <Input
            id="profile-last-name"
            name="lastName"
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            disabled={fetching || saveStatus === "saving"}
            data-cy={CyProfile.LastNameInput}
          />
        </FormGroup>
        <div className="d-flex align-items-center">
          <Button
            color="primary"
            type="submit"
            disabled={fetching || saveStatus === "saving"}
            data-cy={CyProfile.SaveProfileButton}
          >
            {saveStatus === "saving" ? (
              <FontAwesomeIcon icon={faSpinner} spin className="me-1" />
            ) : (
              <FontAwesomeIcon icon={faCheck} className="me-1" />
            )}
            {t("common.buttons.save")}
          </Button>
          {saveStatus === "saved" && (
            <span
              className="text-success ms-3"
              data-cy={CyProfile.SaveStatusAlert}
            >
              <FontAwesomeIcon icon={faCheck} className="me-1" />
              {t("profile.saveSuccess")}
            </span>
          )}
          {saveStatus === "error" && (
            <span
              className="text-danger ms-3"
              data-cy={CyProfile.SaveStatusAlert}
            >
              <FontAwesomeIcon icon={faTimes} className="me-1" />
              {t("profile.saveError")}
            </span>
          )}
        </div>
      </Form>

      <h5>
        <FontAwesomeIcon icon={faKey} className="me-1" />
        {t("profile.permissions")}
      </h5>
      <div>
        {props.auth.getPermissionOverview().map((p, i) => (
          <div key={i} data-cy={CyProfile.PermissionList}>
            {p.granted ? (
              <FontAwesomeIcon icon={faCheck} className="me-1" />
            ) : (
              <FontAwesomeIcon icon={faTimes} className="me-1" />
            )}
            {(t as (key: string) => string)(p.permission.description)}&nbsp;
            <Badge
              color={p.granted ? "success" : "danger"}
              data-cy={
                p.granted
                  ? CyProfile.PermissionGranted
                  : CyProfile.PermissionDeclined
              }
            >
              {p.permission.name}
            </Badge>
            <br />
          </div>
        ))}
      </div>
      <h5>
        <FontAwesomeIcon icon={faLock} className="me-1" />
        {t("profile.logoutTitle")}
      </h5>
      <p>{t("profile.logoutDesc")}</p>
      <Button
        color="primary"
        onClick={() => props.auth.logout(navigate)}
        data-cy={CyProfile.LogoutButton}
      >
        <FontAwesomeIcon icon={faLock} /> {t("auth.logout")}
      </Button>
      <br />
    </ContainerWithHeaderAndMenu>
  );
}
